import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  mapServiceAccountingComment,
  type ServiceAccountingComment,
  type ServiceAccountingOperation,
} from '@entities/service-accounting';
import { useApiServices } from '@shared/lib/use-api-services';

export const useOperationComments = (
  operation: ServiceAccountingOperation,
  expanded = false,
) => {
  const { t } = useTranslation('cabinet');
  const services = useApiServices();
  const [comments, setComments] = useState<ServiceAccountingComment[]>(operation.comments);
  const [commentText, setCommentText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    setComments(operation.comments);
  }, [operation.comments, operation.id]);

  useEffect(() => {
    if (!expanded) {
      return;
    }
    if (comments.length > 0) {
      return;
    }
    if (operation.commentsCount <= 0) {
      return;
    }

    let cancelled = false;
    setIsFetching(true);

    void services.accounting
      .getOperationComments(operation.id)
      .then((items) => {
        if (cancelled) return;
        setComments(items.map(mapServiceAccountingComment));
      })
      .catch(() => {
        if (!cancelled) {
          setComments([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsFetching(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    comments.length,
    expanded,
    operation.commentsCount,
    operation.id,
    services.accounting,
  ]);

  const addComment = useCallback(async () => {
    const text = commentText.trim();
    if (!text) return { success: false as const };

    setIsLoading(true);

    try {
      const created = await services.accounting.addOperationComment(operation.id, text);
      setComments((current) => [...current, mapServiceAccountingComment(created)]);
      setCommentText('');
      return { success: true as const };
    } catch (err: unknown) {
      const message =
        (err as { message?: string })?.message || t('serviceAccounting.comments.addError');
      return { success: false as const, error: message };
    } finally {
      setIsLoading(false);
    }
  }, [commentText, operation.id, services.accounting, t]);

  return {
    comments,
    commentText,
    setCommentText,
    isLoading: isLoading || isFetching,
    addComment,
  };
};
